import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Divider,
  InputBase,
  Popper,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import React from 'react';
import { ChevronLeft } from '@mui/icons-material';

interface Props<T> {
  options: T[];
  selectedOption?: T;
  searchFieldPlaceholder: string;
  isLoading?: boolean;
  nonListOption?: string | React.ReactNode;
  noOptionsText?: string;
  getOptionLabel: (option?: T) => string | undefined;
  getOptionId: (option: T) => number;
  onSelectOption: (optionId?: number) => void;
}

export function GrayStyledAutocomplete<T>({
  options,
  selectedOption,
  searchFieldPlaceholder,
  isLoading,
  noOptionsText,
  nonListOption,
  getOptionLabel,
  getOptionId,
  onSelectOption,
}: Props<T>) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleSelectOption = (optionId?: number) => {
    onSelectOption(optionId);
    setAnchorEl(null);
  };

  const handleClose = () => {
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  return (
    <>
      <Box
        component="button"
        onClick={handleClick}
        sx={(theme) => {
          const palette = theme.vars.palette;
          return {
            borderTopLeftRadius: Number(theme.shape.borderRadius) * 2,
            borderTopRightRadius: Number(theme.shape.borderRadius) * 2,
            bgcolor: `color-mix(in srgb, ${palette.primary.main} 8%, ${palette.background.paper}) !important`,
            border: `1px solid color-mix(in srgb, ${palette.primary.main} 35%, transparent)`,
            borderBottom: 0,
            color: palette.text.primary,
            fontWeight: 600,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: `color-mix(in srgb, ${palette.primary.main} 14%, ${palette.background.paper}) !important`,
            },
            '& .MuiSvgIcon-root': { color: palette.primary.main },
            ...theme.applyStyles('dark', {
              '& .MuiSvgIcon-root': { color: palette.primary.light },
            }),
          };
        }}
        alignItems="center"
        pr={2}
        justifyContent="space-evenly"
        width={498}
        height={45}
        display="flex"
        disabled={!!noOptionsText}
        flexDirection="row"
      >
        <Stack width={478}>
          <Typography fontStyle={noOptionsText ? 'italic' : undefined} variant="body1">
            {isLoading ? (
              <CircularProgress size={14} sx={{ color: 'inherit' }} />
            ) : noOptionsText ? (
              noOptionsText
            ) : (
              getOptionLabel(selectedOption)
            )}
          </Typography>
        </Stack>
        <ChevronLeft
          sx={{
            transform: open ? 'rotate(90deg)' : 'rotate(-90deg)',
            transition: 'transform 0.2s ease-in-out',
          }}
        />
      </Box>
      <StyledPopper open={open} anchorEl={anchorEl} placement="bottom-start">
        <ClickAwayListener onClickAway={handleClose}>
          <div>
            {nonListOption && (
              <>
                <Stack
                  m={2}
                  mb={1}
                  onClick={() => handleSelectOption(undefined)}
                  component={Button}
                  width="fill-available"
                  alignItems="flex-start"
                  borderRadius={3}
                  zIndex={1}
                  sx={{
                    border: '1px solid',
                    borderColor: 'background.paper',
                    '&:hover': {
                      backgroundColor: 'primary.A100',
                      borderColor: 'primary.A100',
                    },
                  }}
                >
                  <Typography variant="body2" color="text.primary">
                    {nonListOption}
                  </Typography>
                </Stack>
                <Divider sx={{ my: 1 }} />
              </>
            )}

            <Autocomplete
              open
              forcePopupIcon={false}
              size="small"
              disablePortal={false}
              getOptionKey={(option) => getOptionId(option)}
              filterSelectedOptions
              isOptionEqualToValue={(option, value) => getOptionId(option) === getOptionId(value)}
              onChange={(_, value) => handleSelectOption(value ? getOptionId(value) : undefined)}
              options={options}
              getOptionLabel={(option) => getOptionLabel(option) ?? '-'}
              renderInput={(params) => (
                <StyledInput
                  ref={params.InputProps.ref}
                  inputProps={params.inputProps}
                  autoFocus
                  placeholder={searchFieldPlaceholder}
                />
              )}
              slots={{ popper: PopperComponent }}
            />
          </div>
        </ClickAwayListener>
      </StyledPopper>
    </>
  );
}

const StyledPopper = styled(Popper)(({ theme }) => ({
  border: `1px solid ${theme.vars.palette.divider}`,
  boxShadow: theme.shadows[1],
  zIndex: theme.zIndex.modal,
  backgroundColor: theme.vars.palette.background.paper,
  borderRadius: Number(theme.shape.borderRadius) * 2,
  width: 500,
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
  padding: theme.spacing(2),
  paddingBottom: 0,
  width: '100%',
  border: 0,
  '& input': {
    borderRadius: Number(theme.shape.borderRadius) * 2,
    padding: theme.spacing(),
    backgroundColor: theme.vars.palette.background.paper,
    border: `1px solid ${theme.vars.palette.divider}`,
  },
}));

interface PopperComponentProps {
  anchorEl?: any;
  disablePortal?: boolean;
  open: boolean;
}

const StyledAutocompletePopper = styled('div')(() => ({
  [`& .MuiPaper-root`]: {
    border: 0,
  },
}));

function PopperComponent(props: PopperComponentProps) {
  const { disablePortal, anchorEl, open, ...other } = props;
  return <StyledAutocompletePopper {...other} />;
}
